import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type WaveSurfer from "wavesurfer.js";
import { guess } from "web-audio-beat-detector";
import {
  computeBeatGridLines,
  CUTTING_POINT_MIN_GAP_MS,
  DEFAULT_BPM,
  formatMs,
  MARQUEE_HIT_TOLERANCE_MS,
  MAX_BPM,
  MAX_ZOOM,
  MIN_BPM,
  MIN_ZOOM,
  normalizeGridOffsetMs,
  pointsEqual,
  SLOW_DRAG_FACTOR,
  snapToBeat,
  ZOOM_STEP,
} from "./cutting-points-utils";

export type Marquee = { leftPct: number; widthPct: number };
export type BarHit = { posMs: number; nonce: number };
export type DragTooltip = { left: number; top: number; label: string };

export type CuttingPointsEditor = ReturnType<typeof useCuttingPointsEditor>;

// Track whether Alt is held so hovering a handle can show a trash cursor.
export function useAltKeyPressed(): boolean {
  const [isAltPressed, setIsAltPressed] = useState(false);

  useEffect(() => {
    const sync = (e: KeyboardEvent) => setIsAltPressed(e.altKey);
    const reset = () => setIsAltPressed(false);
    window.addEventListener("keydown", sync);
    window.addEventListener("keyup", sync);
    window.addEventListener("blur", reset);
    return () => {
      window.removeEventListener("keydown", sync);
      window.removeEventListener("keyup", sync);
      window.removeEventListener("blur", reset);
    };
  }, []);

  return isAltPressed;
}

// Track the rendered waveform width so beat ticks can be thinned out when they
// get too dense. Fires on zoom (width % changes) and container resizes.
export function useWaveformWidth(
  ref: RefObject<HTMLDivElement | null>,
): number {
  const [waveformWidthPx, setWaveformWidthPx] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWaveformWidthPx(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return waveformWidthPx;
}

// Undo/redo history of committed cutting point states.
export function useCuttingPointsHistory(
  cuttingPoints: number[],
  onChangeCuttingPoints: (points: number[]) => void,
) {
  const historyRef = useRef<number[][]>([[...cuttingPoints]]);
  const historyIndexRef = useRef(0);

  // Records a committed cutting point state so it can be undone/redone. Any
  // redo entries ahead of the current position are discarded.
  function pushHistory(points: number[]) {
    const current = historyRef.current[historyIndexRef.current];
    if (current && pointsEqual(current, points)) return;

    const next = historyRef.current.slice(0, historyIndexRef.current + 1);
    next.push([...points]);
    historyRef.current = next;
    historyIndexRef.current = next.length - 1;
  }

  function commit(points: number[]) {
    onChangeCuttingPoints(points);
    pushHistory(points);
  }

  function undo() {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    onChangeCuttingPoints([...historyRef.current[historyIndexRef.current]!]);
  }

  function redo() {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    onChangeCuttingPoints([...historyRef.current[historyIndexRef.current]!]);
  }

  return { pushHistory, commit, undo, redo };
}

// Analyse the decoded audio to guess its BPM and the offset of the first beat.
export function useBeatDetection(
  getWavesurfer: () => WaveSurfer | null,
  onResult: (bpm: number, gridOffsetMs: number) => void,
) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  async function analyzeTrack() {
    const ws = getWavesurfer();
    const audioBuffer = ws?.getDecodedData();
    if (!audioBuffer) return;

    setIsAnalyzing(true);
    try {
      const { bpm, offset } = await guess(audioBuffer);
      const clampedBpm = Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(bpm)));
      onResult(clampedBpm, normalizeGridOffsetMs(offset * 1000, clampedBpm));
    } catch (error) {
      console.error("[CuttingPointsView] beat detection failed", error);
    } finally {
      setIsAnalyzing(false);
    }
  }

  return { analyzeTrack, isAnalyzing };
}

// Owns all of the waveform interaction state, refs and handlers. The view binds
// the returned refs/handlers and renders from the returned state.
export function useCuttingPointsEditor({
  cuttingPoints,
  onChangeCuttingPoints,
  bpm,
  onChangeBpm,
  gridOffsetMs,
  onChangeGridOffsetMs,
}: {
  cuttingPoints: number[];
  onChangeCuttingPoints: (points: number[]) => void;
  bpm: number;
  onChangeBpm: (bpm: number) => void;
  gridOffsetMs: number;
  onChangeGridOffsetMs: (ms: number) => void;
}) {
  const setBpm = onChangeBpm;
  const setGridOffsetMs = onChangeGridOffsetMs;
  const [isLoading, setIsLoading] = useState(true);
  const [songDurationMs, setSongDurationMs] = useState<number | null>(null);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [enableGrid, setEnableGrid] = useState(
    () => bpm !== DEFAULT_BPM || gridOffsetMs !== 0,
  );
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [showGapLabels, setShowGapLabels] = useState(false);
  const [barHit, setBarHit] = useState<BarHit | null>(null);
  const [selectedPoints, setSelectedPoints] = useState<Set<number>>(new Set());
  const [marquee, setMarquee] = useState<Marquee | null>(null);
  const [dragTooltip, setDragTooltip] = useState<DragTooltip | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const lastTimeRef = useRef(0);
  const nonceRef = useRef(0);
  const cuttingPointsRef = useRef(cuttingPoints);
  const selectedPointsRef = useRef(selectedPoints);
  const bpmRef = useRef(bpm);
  const clipboardRef = useRef<number[] | null>(null);
  const selectionRangeRef = useRef<{ startMs: number; endMs: number } | null>(
    null,
  );
  const isScrubbingRef = useRef(false);
  const scrubMsRef = useRef(0);
  const suppressWaveformClickRef = useRef(false);
  const centerOnZoomRef = useRef(false);
  const zoomAnchorRef = useRef<{ ratio: number; pointerOffset: number } | null>(
    null,
  );
  const zoomGestureRef = useRef<{
    ratio: number;
    pointerOffset: number;
  } | null>(null);
  const zoomGestureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const waveformWidthPx = useWaveformWidth(waveformRef);
  const {
    pushHistory,
    commit: commitCuttingPoints,
    undo,
    redo,
  } = useCuttingPointsHistory(cuttingPoints, onChangeCuttingPoints);
  const { analyzeTrack, isAnalyzing } = useBeatDetection(
    () => wavesurferRef.current,
    (nextBpm, nextGridOffsetMs) => {
      setBpm(nextBpm);
      setGridOffsetMs(nextGridOffsetMs);
    },
  );

  // Keep the ref current for the wavesurfer handlers without mutating during render.
  useEffect(() => {
    cuttingPointsRef.current = cuttingPoints;
  }, [cuttingPoints]);

  useEffect(() => {
    return () => {
      try {
        wavesurferRef.current?.stop();
      } catch {
        // The player may already be torn down by its wrapper.
      }
    };
  }, []);

  useEffect(() => {
    selectedPointsRef.current = selectedPoints;
  }, [selectedPoints]);

  useEffect(() => {
    bpmRef.current = bpm;
  }, [bpm]);

  // Prune the selection when points are removed or moved away.
  useEffect(() => {
    setSelectedPoints((prev) => {
      const next = new Set([...prev].filter((p) => cuttingPoints.includes(p)));
      return next.size === prev.size ? prev : next;
    });
  }, [cuttingPoints]);

  // Clear the selection when the pointer goes down outside the waveform
  // container, mirroring the click-to-clear behaviour on the waveform itself.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (selectedPointsRef.current.size === 0) return;
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) {
        selectionRangeRef.current = null;
        setSelectedPoints(new Set());
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, []);

  // Zoom the waveform with Cmd/Ctrl + scroll. A native non-passive listener is
  // used so the browser's page-zoom can be prevented.
  useEffect(() => {
    const scroller = scrollContainerRef.current;
    if (!scroller) return;

    const onWheel = (e: WheelEvent) => {
      if (!e.metaKey && !e.ctrlKey) return;
      e.preventDefault();

      // Capture the point under the cursor once per gesture and keep it as the
      // anchor until scrolling settles, so it stays locked to the cursor.
      const content = waveformRef.current;
      if (content && !zoomGestureRef.current) {
        const contentRect = content.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        zoomGestureRef.current = {
          ratio: (e.clientX - contentRect.left) / content.offsetWidth,
          pointerOffset: e.clientX - scrollerRect.left,
        };
      }
      zoomAnchorRef.current = zoomGestureRef.current;

      // Reset the gesture shortly after the last wheel event.
      if (zoomGestureTimerRef.current)
        clearTimeout(zoomGestureTimerRef.current);
      zoomGestureTimerRef.current = setTimeout(() => {
        zoomGestureRef.current = null;
      }, 200);

      setZoom((z) =>
        Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z - e.deltaY * 0.01)),
      );
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      scroller.removeEventListener("wheel", onWheel);
      if (zoomGestureTimerRef.current)
        clearTimeout(zoomGestureTimerRef.current);
    };
  }, []);

  // Navigate between cutting points with the ,< and .> keys, play/pause the
  // audio with the space bar, and add a cutting point at the playhead with f.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }

      if (e.key === "<" || e.key === ",") {
        e.preventDefault();
        goToPreviousCuttingPoint();
      } else if (e.key === ">" || e.key === ".") {
        e.preventDefault();
        goToNextCuttingPoint();
      } else if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        togglePlayback();
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        addCuttingPointAtPlayhead();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // The handlers only read refs, so a one-time registration is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Undo/redo cutting point edits with Cmd/Ctrl+Z and Cmd/Ctrl+Shift+Z (or Y).
  // Registered in the capture phase and stops propagation so the editor's
  // global composition undo/redo does not also fire.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) return;

      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        e.stopPropagation();
        redo();
      } else if (key === "c") {
        const selected = [...selectedPointsRef.current].sort((a, b) => a - b);
        if (selected.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        // Base offsets on the drag rectangle's start when available so the gap
        // before the first point is preserved on paste; otherwise use the
        // first selected point.
        const range = selectionRangeRef.current;
        const origin = range ? range.startMs : selected[0]!;
        clipboardRef.current = selected.map((p) => p - origin);
      } else if (key === "v") {
        if (!clipboardRef.current?.length) return;
        e.preventDefault();
        e.stopPropagation();
        pasteCuttingPointsAtPlayhead();
      } else if (key === "d") {
        if (selectedPointsRef.current.size === 0) return;
        e.preventDefault();
        e.stopPropagation();
        duplicateSelectedPoints();
      } else if (key === "g" && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        moveGridToPlayhead();
      } else if (key === "s") {
        e.preventDefault();
        e.stopPropagation();
        setSnapToGrid((prev) => !prev);
      } else if (key === "h") {
        e.preventDefault();
        e.stopPropagation();
        setEnableGrid((prev) => !prev);
      } else if (key === "l") {
        e.preventDefault();
        e.stopPropagation();
        setShowGapLabels((prev) => !prev);
      } else if (key === "backspace" || key === "delete") {
        if (selectedPointsRef.current.size === 0) return;
        e.preventDefault();
        e.stopPropagation();
        removeSelectedPoints();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
    // The handlers only read refs, so a one-time registration is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep a meaningful anchor in view when the zoom level changes. Zoom control
  // buttons center on the playhead; Cmd/Ctrl+scroll keeps the point under the
  // cursor fixed. Other scroll positions are left untouched.
  useLayoutEffect(() => {
    const scroller = scrollContainerRef.current;
    const content = waveformRef.current;
    const ws = wavesurferRef.current;
    if (!scroller || !content) return;

    const clampScroll = (target: number) =>
      Math.max(0, Math.min(target, content.offsetWidth - scroller.clientWidth));

    if (centerOnZoomRef.current) {
      centerOnZoomRef.current = false;
      const duration = ws?.getDuration();
      if (!ws || !duration) return;

      const ratio = ws.getCurrentTime() / duration;
      scroller.scrollLeft = clampScroll(
        ratio * content.offsetWidth - scroller.clientWidth / 2,
      );
      return;
    }

    const anchor = zoomAnchorRef.current;
    if (anchor) {
      zoomAnchorRef.current = null;
      scroller.scrollLeft = clampScroll(
        anchor.ratio * content.offsetWidth - anchor.pointerOffset,
      );
    }
  }, [zoom]);

  function onReady(ws: WaveSurfer) {
    wavesurferRef.current = ws;
    setSongDurationMs(Math.round(ws.getDuration() * 1000));
    setIsLoading(false);
  }

  // The duration can be refined after the initial "ready" (e.g. VBR MP3), so
  // keep it in sync with wavesurfer's value to stop handles drifting from the
  // playhead later in the track.
  function handleDecode(ws: WaveSurfer) {
    const ms = Math.round(ws.getDuration() * 1000);
    setSongDurationMs((prev) => (prev === ms ? prev : ms));
  }

  // Pulse a cutting point when playback naturally advances past it. Manual
  // seeks report a large jump (and update lastTimeRef via onSeeking), so they
  // are ignored.
  function handleTimeUpdate(_ws: WaveSurfer, currentTime: number) {
    // While scrubbing, the playhead position is driven by the drag, not by
    // playback, so ignore the natural time advance.
    if (isScrubbingRef.current) {
      lastTimeRef.current = currentTime;
      return;
    }

    const prevMs = lastTimeRef.current * 1000;
    const currentMs = currentTime * 1000;
    lastTimeRef.current = currentTime;
    setCurrentTimeMs(currentMs);

    const deltaMs = currentMs - prevMs;
    if (deltaMs <= 0 || deltaMs > 500) return;

    const crossed = cuttingPointsRef.current.find(
      (p) => p > prevMs && p <= currentMs,
    );
    if (crossed !== undefined) {
      setBarHit({ posMs: crossed, nonce: nonceRef.current++ });
    }
  }

  function handleSeeking(_ws: WaveSurfer, currentTime: number) {
    lastTimeRef.current = currentTime;
    setCurrentTimeMs(currentTime * 1000);
  }

  function handleError(error: unknown) {
    console.error("[CuttingPointsView] wavesurfer error", error);
    setIsLoading(false);
  }

  function msFromClientX(clientX: number): number | null {
    const content = waveformRef.current;
    if (!content || !songDurationMs) return null;
    const rect = content.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * songDurationMs);
  }

  // Scroll the waveform so the given time (ms) is visible, but only when it is
  // currently off-screen (or within a small edge margin). Centers the position
  // in the viewport. Used when a cutting point lands somewhere not in view.
  function scrollMsIntoView(ms: number) {
    const scroller = scrollContainerRef.current;
    const content = waveformRef.current;
    if (!scroller || !content || !songDurationMs) return;

    const x = (ms / songDurationMs) * content.offsetWidth;
    const margin = scroller.clientWidth * 0.1;
    if (
      x >= scroller.scrollLeft + margin &&
      x <= scroller.scrollLeft + scroller.clientWidth - margin
    ) {
      return;
    }
    scroller.scrollLeft = Math.max(
      0,
      Math.min(
        x - scroller.clientWidth / 2,
        content.offsetWidth - scroller.clientWidth,
      ),
    );
  }

  function snap(ms: number): number {
    return snapToBeat(ms, {
      enableGrid,
      snapToGrid,
      bpm,
      gridOffsetMs,
      songDurationMs,
    });
  }

  // A single click on the waveform moves the playhead to that position.
  function handleWaveformClick(e: React.MouseEvent) {
    // Ignore the click that trails a handle drag.
    if (suppressWaveformClickRef.current) {
      suppressWaveformClickRef.current = false;
      return;
    }

    // A plain click on the waveform clears the selection.
    if (selectedPointsRef.current.size > 0) {
      selectionRangeRef.current = null;
      setSelectedPoints(new Set());
    }

    // Alt is used to delete handles, so don't move the playhead then.
    if (e.altKey) return;

    const ms = msFromClientX(e.clientX);
    if (ms === null || !songDurationMs) return;

    // Snap the playhead to the closest beat when snap-to-grid is on.
    wavesurferRef.current?.seekTo(snap(ms) / songDurationMs);
  }

  function addCuttingPoint(ms: number) {
    const isTooClose = cuttingPoints.some(
      (point) => Math.abs(point - ms) < CUTTING_POINT_MIN_GAP_MS,
    );
    if (isTooClose) return;

    commitCuttingPoints([...cuttingPoints, ms].sort((a, b) => a - b));
    scrollMsIntoView(ms);
  }

  // Double-click on the waveform adds a cutting point.
  function handleWaveformDoubleClick(e: React.MouseEvent) {
    const ms = msFromClientX(e.clientX);
    if (ms === null) return;

    // Snap the added point to the closest beat when snap-to-grid is on.
    addCuttingPoint(Math.round(snap(ms)));
  }

  // Add a cutting point at the current playhead position. The live time is read
  // from wavesurfer so it stays accurate during playback. Reads the current
  // points from the ref because this runs from a one-time keydown listener with
  // a stale `cuttingPoints` closure.
  function addCuttingPointAtPlayhead() {
    const ws = wavesurferRef.current;
    if (!ws) return;

    // While scrubbing, use the loop-start position instead of the live playback
    // time, which advances within the 500ms loop.
    const ms = isScrubbingRef.current
      ? Math.round(scrubMsRef.current)
      : Math.round(ws.getCurrentTime() * 1000);
    const points = cuttingPointsRef.current;
    const isTooClose = points.some(
      (point) => Math.abs(point - ms) < CUTTING_POINT_MIN_GAP_MS,
    );
    if (isTooClose) return;

    commitCuttingPoints([...points, ms].sort((a, b) => a - b));
    scrollMsIntoView(ms);
  }

  // Drag on an empty part of the waveform to select the cutting points inside
  // the rectangle. Holding shift keeps the existing selection and adds to it.
  function handleWaveformPointerDown(e: React.PointerEvent) {
    if (e.button !== 0 || e.altKey) return;
    const content = waveformRef.current;
    if (!content || !songDurationMs) return;

    const rect = content.getBoundingClientRect();
    const startX = e.clientX;
    const selectionAtStart = e.shiftKey
      ? new Set(selectedPointsRef.current)
      : new Set<number>();
    let dragging = false;

    const onMove = (ev: PointerEvent) => {
      // A small threshold keeps click-to-seek and double-click-to-add working.
      if (!dragging && Math.abs(ev.clientX - startX) < 4) return;
      dragging = true;

      const a = Math.max(rect.left, Math.min(startX, ev.clientX));
      const b = Math.min(rect.right, Math.max(startX, ev.clientX));
      // Snap the rectangle edges to the nearest beat when snap-to-grid is on.
      const startMs = snap(((a - rect.left) / rect.width) * songDurationMs);
      const endMs = snap(((b - rect.left) / rect.width) * songDurationMs);

      setMarquee({
        leftPct: (startMs / songDurationMs) * 100,
        widthPct: ((endMs - startMs) / songDurationMs) * 100,
      });
      selectionRangeRef.current = { startMs, endMs };
      const inRange = cuttingPointsRef.current.filter(
        (p) =>
          p >= startMs - MARQUEE_HIT_TOLERANCE_MS &&
          p <= endMs + MARQUEE_HIT_TOLERANCE_MS,
      );
      setSelectedPoints(new Set([...selectionAtStart, ...inRange]));
    };

    const onUp = () => {
      if (dragging) {
        // Don't let the trailing click seek the playhead or clear the selection.
        suppressWaveformClickRef.current = true;
        setTimeout(() => {
          suppressWaveformClickRef.current = false;
        }, 0);
      }
      setMarquee(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // Paste copied points re-based so the first one lands on the playhead (or the
  // scrub loop-start while dragging the playhead). Values that fall outside the
  // track or too close to an existing point are dropped. Reads everything from
  // refs/wavesurfer because this runs from a one-time keydown listener.
  function pasteCuttingPointsAtPlayhead() {
    const ws = wavesurferRef.current;
    const offsets = clipboardRef.current;
    if (!ws || !offsets?.length) return;

    const durationMs = ws.getDuration() * 1000;
    const baseMs = isScrubbingRef.current
      ? scrubMsRef.current
      : ws.getCurrentTime() * 1000;

    const points = cuttingPointsRef.current;
    const added = offsets
      .map((offset) => Math.round(baseMs + offset))
      .filter(
        (ms) =>
          ms >= 0 &&
          ms <= durationMs &&
          points.every((p) => Math.abs(p - ms) >= CUTTING_POINT_MIN_GAP_MS),
      );
    if (added.length === 0) return;

    commitCuttingPoints([...points, ...added].sort((a, b) => a - b));
    setSelectedPoints(new Set(added));
    scrollMsIntoView(Math.min(...added));
  }

  // Remove all selected cutting points as a single undoable step. Reads from
  // refs because this runs from a one-time keydown listener.
  function removeSelectedPoints() {
    const selected = selectedPointsRef.current;
    if (selected.size === 0) return;

    commitCuttingPoints(
      cuttingPointsRef.current.filter((point) => !selected.has(point)),
    );
    selectionRangeRef.current = null;
    setSelectedPoints(new Set());
  }

  function duplicateSelectedPoints() {
    const ws = wavesurferRef.current;
    const selected = [...selectedPointsRef.current].sort((a, b) => a - b);
    if (!ws || selected.length === 0) return;

    const durationMs = ws.getDuration() * 1000;
    const range = selectionRangeRef.current;
    // Copy the rectangle span when available, so leading/trailing gaps are
    // kept; fall back to spanning the first/last selected point otherwise.
    const origin = range ? range.startMs : selected[0]!;
    const spanEnd = range ? range.endMs : selected[selected.length - 1]!;
    const shift = spanEnd - origin;

    const points = cuttingPointsRef.current;
    const added = selected
      .map((point) => Math.round(point + shift))
      .filter(
        (ms) =>
          ms >= 0 &&
          ms <= durationMs &&
          points.every((p) => Math.abs(p - ms) >= CUTTING_POINT_MIN_GAP_MS),
      );
    if (added.length === 0) return;

    commitCuttingPoints([...points, ...added].sort((a, b) => a - b));
    // Advance the range so repeated duplication keeps tiling forward.
    if (range) {
      selectionRangeRef.current = {
        startMs: range.startMs + shift,
        endMs: range.endMs + shift,
      };
    }
    setSelectedPoints(new Set(added));
    scrollMsIntoView(Math.min(...added));
  }

  function togglePlayback() {
    const ws = wavesurferRef.current;
    if (!ws) return;

    if (ws.isPlaying()) {
      const timeSec = ws.getCurrentTime();
      ws.pause();
      ws.setTime(timeSec);
      setCurrentTimeMs(timeSec * 1000);
      return;
    }

    void ws.play();
  }

  // Drag the playhead to scrub. While dragging, the ~500ms after the playhead
  // loops so you can hear that spot. Holding cmd/ctrl slows the movement for
  // finer control, like dragging a handle.
  function handlePlayheadPointerDown(e: React.PointerEvent) {
    const content = waveformRef.current;
    const ws = wavesurferRef.current;
    if (!content || !songDurationMs || !ws) return;
    // Ignore the second press of a double-click. The first click moves the
    // playhead under the cursor, so the second press lands on the playhead and
    // would briefly start the scrub preview. Letting it bubble also keeps
    // double-click-to-add working.
    if (e.detail > 1) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = content.getBoundingClientRect();
    const rectWidth = rect.width;
    let currentMs = currentTimeMs;
    // Raw (unsnapped) accumulator so dragging stays smooth; currentMs holds the
    // snapped value used for seeking and display.
    let rawMs = currentMs;
    let lastClientX = e.clientX;

    isScrubbingRef.current = true;
    scrubMsRef.current = currentMs;

    const seekToCurrent = () => {
      ws.seekTo(Math.max(0, Math.min(1, currentMs / songDurationMs)));
    };

    // Wait 100ms before starting the looping preview so a quick click (or the
    // second press of a double-click) doesn't briefly play the track.
    seekToCurrent();
    let loopInterval: number | null = null;
    const loopStartTimer = window.setTimeout(() => {
      void ws.play();
      // Restart playback at the playhead every 500ms to loop that position.
      loopInterval = window.setInterval(() => {
        seekToCurrent();
        if (!ws.isPlaying()) void ws.play();
      }, 500);
    }, 100);

    const onMove = (ev: PointerEvent) => {
      const deltaPx = ev.clientX - lastClientX;
      lastClientX = ev.clientX;

      const speed = ev.metaKey || ev.ctrlKey ? SLOW_DRAG_FACTOR : 1;
      rawMs = Math.max(
        0,
        Math.min(
          songDurationMs,
          rawMs + (deltaPx / rectWidth) * songDurationMs * speed,
        ),
      );
      currentMs = snap(rawMs);
      setCurrentTimeMs(currentMs);
      scrubMsRef.current = currentMs;
    };

    const onUp = () => {
      window.clearTimeout(loopStartTimer);
      if (loopInterval !== null) window.clearInterval(loopInterval);
      ws.pause();
      isScrubbingRef.current = false;
      seekToCurrent();
      setCurrentTimeMs(currentMs);
      // The pointer may lift over the waveform (e.g. after a slow cmd drag),
      // which would fire a click that seeks the playhead. Suppress it.
      suppressWaveformClickRef.current = true;
      setTimeout(() => {
        suppressWaveformClickRef.current = false;
      }, 0);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // Seek to the closest cutting point before the playhead (or the track start).
  // The live time is read from wavesurfer so it stays accurate during playback.
  // A cutting point within 500ms behind the playhead counts as "here", so back
  // skips to the one before it instead of getting stuck on it.
  function goToPreviousCuttingPoint() {
    const ws = wavesurferRef.current;
    const duration = ws?.getDuration();
    if (!ws || !duration) return;

    const currentMs = ws.getCurrentTime() * 1000;
    const previous = [...cuttingPointsRef.current]
      .sort((a, b) => a - b)
      .filter((point) => point < currentMs - 500)
      .pop();

    ws.seekTo(Math.max(0, (previous ?? 0) / 1000 / duration));
  }

  // Seek to the closest cutting point after the playhead. Does nothing when
  // there is no later cutting point so it won't jump to the end of the track.
  function goToNextCuttingPoint() {
    const ws = wavesurferRef.current;
    const duration = ws?.getDuration();
    if (!ws || !duration) return;

    const currentMs = ws.getCurrentTime() * 1000;
    const next = [...cuttingPointsRef.current]
      .sort((a, b) => a - b)
      .find((point) => point > currentMs + 10);

    if (next === undefined) return;

    ws.seekTo(Math.min(1, next / 1000 / duration));
  }

  // Drag a handle to move its cutting point. Holding cmd/ctrl slows the
  // movement down for accuracy.
  function makeHandlePointerDown(index: number) {
    return (e: React.PointerEvent) => {
      const content = waveformRef.current;
      if (!content || !songDurationMs) return;
      e.preventDefault();
      e.stopPropagation();

      // Shift-click toggles the point in the multi-selection instead of
      // starting a drag.
      if (e.shiftKey) {
        const value = cuttingPoints[index]!;
        selectionRangeRef.current = null;
        setSelectedPoints((prev) => {
          const next = new Set(prev);
          if (next.has(value)) next.delete(value);
          else next.add(value);
          return next;
        });
        return;
      }

      // Holding Alt turns a press into a direct removal.
      if (e.altKey) {
        handleRemove(index);
        return;
      }

      setDragTooltip(null);

      const rect = content.getBoundingClientRect();
      const rectWidth = rect.width;
      // Raw (unsnapped) accumulator so dragging stays smooth; the committed
      // position is snapped to the beat when snap-to-grid is on.
      let rawMs = cuttingPoints[index] ?? 0;
      let lastClientX = e.clientX;

      const showTooltip = (ms: number) => {
        setDragTooltip({
          left: rect.left + (ms / songDurationMs) * rectWidth,
          top: rect.top,
          label: formatMs(ms),
        });
      };

      showTooltip(Math.round(rawMs));

      let latestPoints = cuttingPoints;

      // When the dragged handle is one of several selected points, move the
      // whole selection together, preserving the spacing between the points.
      const draggedValue = cuttingPoints[index]!;
      const selectedAtStart = selectedPointsRef.current;
      const isGroupDrag =
        selectedAtStart.size > 1 && selectedAtStart.has(draggedValue);
      const groupValues = [...selectedAtStart];
      const groupMin = groupValues.length ? Math.min(...groupValues) : 0;
      const groupMax = groupValues.length ? Math.max(...groupValues) : 0;
      let groupShift = 0;
      // The dragged handle's final position, so the view can follow it on drop.
      let finalMs = cuttingPoints[index] ?? 0;

      const onMove = (ev: PointerEvent) => {
        const deltaPx = ev.clientX - lastClientX;
        lastClientX = ev.clientX;

        const speed = ev.metaKey || ev.ctrlKey ? SLOW_DRAG_FACTOR : 1;
        rawMs = Math.max(
          0,
          Math.min(
            songDurationMs,
            rawMs + (deltaPx / rectWidth) * songDurationMs * speed,
          ),
        );

        const ms = Math.round(snap(rawMs));

        if (isGroupDrag) {
          // Shift every selected point by the dragged handle's movement,
          // clamped so the whole group stays within the track.
          groupShift = Math.max(
            -groupMin,
            Math.min(songDurationMs - groupMax, ms - draggedValue),
          );
          const shifted = new Map(groupValues.map((v) => [v, v + groupShift]));
          latestPoints = cuttingPoints
            .map((point) => shifted.get(point) ?? point)
            .sort((a, b) => a - b);
          onChangeCuttingPoints(latestPoints);
          setSelectedPoints(new Set(groupValues.map((v) => v + groupShift)));
          showTooltip(draggedValue + groupShift);
          return;
        }

        latestPoints = cuttingPoints.map((point, i) =>
          i === index ? ms : point,
        );
        onChangeCuttingPoints(latestPoints);
        finalMs = ms;
        showTooltip(ms);
      };

      const onUp = () => {
        setDragTooltip(null);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        // The pointer may lift over the waveform (e.g. after a slow cmd drag),
        // which would fire a click that seeks the playhead. Suppress it.
        suppressWaveformClickRef.current = true;
        setTimeout(() => {
          suppressWaveformClickRef.current = false;
        }, 0);
        // Keep the drag-select range aligned with the moved group so a later
        // duplicate/copy uses the new positions.
        if (isGroupDrag && groupShift !== 0 && selectionRangeRef.current) {
          selectionRangeRef.current = {
            startMs: selectionRangeRef.current.startMs + groupShift,
            endMs: selectionRangeRef.current.endMs + groupShift,
          };
        }
        // Record the final position as a single undoable step.
        pushHistory(latestPoints);
        // Follow the moved handle(s) into view: the first handle of a group
        // drag, otherwise the single dragged handle.
        scrollMsIntoView(isGroupDrag ? groupMin + groupShift : finalMs);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    };
  }

  function handleRemove(index: number) {
    commitCuttingPoints(cuttingPoints.filter((_, i) => i !== index));
  }

  function zoomIn() {
    centerOnZoomRef.current = true;
    setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
  }

  function zoomOut() {
    centerOnZoomRef.current = true;
    setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
  }

  // Align the beat grid to the current playhead. Bound to Cmd/Ctrl+G. The
  // offset is normalised to a single bar so the value stays small. Reads from
  // refs because it runs from a one-time keydown listener.
  function moveGridToPlayhead() {
    const ws = wavesurferRef.current;
    if (!ws || bpmRef.current <= 0) return;
    const ms = isScrubbingRef.current
      ? scrubMsRef.current
      : ws.getCurrentTime() * 1000;
    setGridOffsetMs(normalizeGridOffsetMs(ms, bpmRef.current));
  }

  const beatGridLines = computeBeatGridLines(
    songDurationMs,
    bpm,
    gridOffsetMs,
    waveformWidthPx,
  );

  const handlersRef = useRef({
    onReady,
    handleDecode,
    handleTimeUpdate,
    handleSeeking,
    handleError,
  });
  handlersRef.current = {
    onReady,
    handleDecode,
    handleTimeUpdate,
    handleSeeking,
    handleError,
  };
  const wavesurferHandlers = useMemo(
    () => ({
      onReady: (ws: WaveSurfer) => handlersRef.current.onReady(ws),
      onDecode: (ws: WaveSurfer) => handlersRef.current.handleDecode(ws),
      onTimeupdate: (ws: WaveSurfer, currentTime: number) =>
        handlersRef.current.handleTimeUpdate(ws, currentTime),
      onSeeking: (ws: WaveSurfer, currentTime: number) =>
        handlersRef.current.handleSeeking(ws, currentTime),
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onFinish: () => setIsPlaying(false),
      onError: (_ws: WaveSurfer, error: unknown) =>
        handlersRef.current.handleError(error),
    }),
    [],
  );

  return {
    // refs to attach
    containerRef,
    scrollContainerRef,
    waveformRef,
    // state
    isLoading,
    songDurationMs,
    currentTimeMs,
    isPlaying,
    zoom,
    enableGrid,
    setEnableGrid,
    snapToGrid,
    setSnapToGrid,
    showGapLabels,
    setShowGapLabels,
    selectedPoints,
    marquee,
    barHit,
    dragTooltip,
    isAnalyzing,
    // derived
    beatGridLines,
    // wavesurfer bindings
    wavesurferHandlers,
    // handlers
    handleWaveformClick,
    handleWaveformDoubleClick,
    handleWaveformPointerDown,
    handlePlayheadPointerDown,
    makeHandlePointerDown,
    handleRemove,
    togglePlayback,
    zoomIn,
    zoomOut,
    analyzeTrack,
  };
}
